import { CollectionInterface, CollectionThumbnail,CollectionUserImages, CollectionUserMaterials, MeasuringCategory, ProfileInterface, validateCollection, UserProfileRole } from 'ts-interfaces'
import { create } from 'zustand'
import { deleteRecordAndFiles, firebaseDB, profilesDB, updateRecord, uploadFileToGCloud, userCollectionsDB } from '../../libs/firebase'
import { produce } from 'immer'
import { doc, getDoc, getDocs, limit, orderBy, query, startAfter, where, documentId, collection } from 'firebase/firestore'
import { getUserLocationFromIP,generateID, getUserLocationFromIPdata } from "../helper";
import { updateProfile } from '../../libs/util'
import { firebaseHelper, usePermissions, useDesignFirm } from 'ui'
import { Permissions } from "ts-interfaces";
import { useCollectionDetailed } from '../../global-state/useCollectionDetailed';


export interface LoginData {
  user: any
  providerId?: string
  _tokenResponse?: any
}

export interface LoginState {
  profile: ProfileInterface | null
  showLogin: any
  collections: CollectionInterface[]
  writeableCollections: CollectionInterface[]
  
  // Teams PRO access state
  isProUser: boolean
  isTeamsProUser: boolean

}


let globalLoginStateIsProcessing=false; 


export const useLoginState = create( (set:any, get:any) => ({
  profile:null,
  showLogin:null,
  collections:[],
  writeableCollections:[],
  collectionsFullyLoaded: false,
  processing:false,
  firebaseDoc:null,
  permissions: null,
  
  // Teams PRO access state
  isProUser: false,
  isTeamsProUser: false,



  getPermmissions: async (skipTeamsProUpdate = false): Promise<Permissions | null> => {
    if (get().permissions) return get().permissions 
    const uid = get().profile?.uid 
    if (get().profile?.uid == null) return null;
    const docRef = doc(firebaseHelper.db, 'permissions', uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data() as Permissions;
      set({permissions:data})
      // Update Teams PRO access when permissions are loaded (unless we're already updating it)
      if (!skipTeamsProUpdate) {
        get().updateTeamsProAccess()
      }
      return data
    }
    else { return null; }
  },

  updateWriteableCollections: async () => {
    const state = get();
    const currentUserUid = state.profile?.uid;
    
    if (!currentUserUid || !state.collections.length) {
      set({writeableCollections: []});
      return;
    }

    try {
      const writeableCollectionsPromises = state.collections.map(async (collection) => {
        const canWrite = await checkCollectionWritePermission(currentUserUid, collection);
        return canWrite ? collection : null;
      });

      const results = await Promise.all(writeableCollectionsPromises);
      const filteredCollections = results.filter((collection) => collection !== null) as CollectionInterface[];
      
      set({writeableCollections: filteredCollections});
    } catch (error) {
      console.error('Error updating writeable collections:', error);
      set({writeableCollections: []});
    }
  },

  // Update Teams PRO access status
  updateTeamsProAccess: async () => {
    const state = get();
    const profile = state.profile;
    const currentUserUid = profile?.uid;
    
    if (!currentUserUid || !profile) {
      set({
        isProUser: false,
        isTeamsProUser: false,
      });
      return;
    }

    try {
      // Check if user is PRO
      const isProUser = profile && (profile as any)?.userRole === UserProfileRole.PRO;

      // Get permissions if not already loaded
      let permissions = state.permissions;
      if (!permissions) {
        permissions = await get().getPermmissions(true); // Skip teams pro update to prevent infinite loop
      }
      
      // Get design firm info and check Teams PRO status
      let designFirm: any = null;
      let isTeamsProUser = false;
      
      if (permissions?.teamsDesigner?.[0]) {
        // This is a simplified version - in real implementation this would use the useDesignFirm hook
        // For now, we'll just check if they have teamsDesigner permission
        designFirm = { firm: { name: 'Design Firm' } }; // Placeholder for design firm info
        
        // Query the teams collection to find if user is part of an active team
        try {
          const teamsCollection = collection(firebaseHelper.db, 'teams');
          const teamQuery = query(teamsCollection, where("uid", "==", currentUserUid));
          const teamSnapshot = await getDocs(teamQuery);
          
          // Check if any team document has active status
          for (const teamDoc of teamSnapshot.docs) {
            const teamData = teamDoc.data();
            if (teamData?.status === 'active') {
              isTeamsProUser = true;
              break; // Found an active team, no need to check further
            }
          }
        } catch (error) {
          console.error('Error checking team status:', error);
        }
      }
      
      // Final Teams PRO determination: must be PRO user AND have design firm access
      isTeamsProUser = isProUser && isTeamsProUser && Boolean(designFirm?.firm?.name);

      set({
        isProUser,
        isTeamsProUser
      });
      
    } catch (error) {
      console.error('Error updating Teams PRO access:', error);
      set({
        isProUser: false,
        isTeamsProUser: false,
      });
    }
  },

  setShowLogin: (showLogin: any)=> set({showLogin}),
  setProfile: (profile:any) => {    
    set({processing:false})
    if (!profile || !profile?.id) { 
      set({
        firebaseDoc:null,
        permissions:null, 
        profile:null, 
        collections:[], 
        writeableCollections:[], 
        processing:false, 
        collectionsFullyLoaded:false,
        isProUser: false,
        isTeamsProUser: false,
      })
    }
    else {       
      set({profile:profile})
      // Update Teams PRO access when profile is set
      get().updateTeamsProAccess()
    }
  },
  setIncrementRenderCount: async () => {
    if (globalLoginStateIsProcessing==true) return;
    const profile = get().profile
    if (!profile) return;
    try {
      globalLoginStateIsProcessing=true;
      const newProfile:ProfileInterface |any = produce(profile, (draft:ProfileInterface) => {
        draft.renders = draft.renders ? draft.renders + 1 : 1
      })
      await get().updateAndSaveProfile(newProfile)
    }
    finally { globalLoginStateIsProcessing=false; }
  },  
  //replaces the createProfileIfNotExists function
  //only useful when we have loginData
  //most important thing.. if i end up createing a profile 
  //Only one scenario where this is a problem -> user signups, then logs out, then logs in really fast. 
  setOrCreateProfile: async (loginData:LoginData, mailingList=false) => {
    if (get().profile?.id || get().processing==true) return;
    if (globalLoginStateIsProcessing==true) return;
    set({processing:true})
    try {
      globalLoginStateIsProcessing = true
      const resp = await createProfileIfNotExists(loginData, mailingList)
      if (resp?.profile?.id) { 
        set({ firebaseDoc:resp.ref}) 
        get().setProfile(resp.profile) // Use setProfile method to trigger updateTeamsProAccess
      }
      else { 
        get().setProfile(null) // Use setProfile method to clear state properly
      }
      return resp;
    }
    finally { set({processing:false}); globalLoginStateIsProcessing=false; }
  },

  getUserProfile: async (loginData:LoginData) => {
    if (get().profile?.id || get().processing==true) return;
    if (globalLoginStateIsProcessing==true) return;
    set({processing:true})
    try {
      globalLoginStateIsProcessing = true
      const resp = await getProfile(loginData)
      if (resp?.profile?.id) { 
        set({ firebaseDoc:resp.ref}) 
        get().setProfile(resp.profile) // Use setProfile method to trigger updateTeamsProAccess
      }
      else { 
        get().setProfile(null) // Use setProfile method to clear state properly
      }
      return resp;
    }
    finally { set({processing:false}); globalLoginStateIsProcessing=false; }
  },

  updateAndSaveProfile: async (profile:ProfileInterface, pic={}) => {
    return new Promise((resolve, reject) => {
      if (get().profile?.uid == null) reject("User not logged in")
      if (!get().firebaseDoc) reject("No profile reference found")
      if (get().processing==true) reject("Processing") 
      updateProfile(profile,pic, get().firebaseDoc as any).then(result=>{
        set({profile:result})
        resolve(result)
      }).catch(error=> { console.log("error ", error); reject(error) })
    })
  },

  getMoreCollections: async (limit=40) => {
    try {
      if (get().collectionsFullyLoaded===true || get().processing==true) return;
      if (get().profile?.uid == null) return;
      set({processing:true} )
      
      // Fetch personal collections (owned by user)
      const morePersonalCollections = await getCollections(get().profile.uid, get().collections.filter(c => c.uid === get().profile.uid), Math.ceil(limit/4))
      
      // Fetch shared individual collections (not project-based)
      const moreSharedCollections = await getSharedCollections(get().profile.uid, get().collections.filter(c => c.uid !== get().profile.uid && !c.associatedProjectId), Math.ceil(limit/4))
      
      // Fetch owned project collections
      const moreOwnedProjectCollections = await getOwnedProjectCollections(get().profile.uid, get().collections.filter(c => c.associatedProjectId), Math.ceil(limit/4))
      
      // Fetch shared project collections
      const moreSharedProjectCollections = await getSharedProjectCollections(get().profile.uid, get().collections.filter(c => c.associatedProjectId), Math.ceil(limit/4))
      
      // Combine all new collections
      const allNewCollections = [...morePersonalCollections, ...moreSharedCollections, ...moreOwnedProjectCollections, ...moreSharedProjectCollections]
      
      set(produce( (state:any) => {
        // Merge all collections into single array
        const mergedCollections = [...state.collections, ...allNewCollections]
        state.collections = mergedCollections.filter((v,i,a)=>a.findIndex(t=>(t.id === v.id))===i)
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        
        // Mark as fully loaded if all four types returned fewer than requested
        if (morePersonalCollections.length < Math.ceil(limit/4) && 
            moreSharedCollections.length < Math.ceil(limit/4) && 
            moreOwnedProjectCollections.length < Math.ceil(limit/4) &&
            moreSharedProjectCollections.length < Math.ceil(limit/4)) {
          state.collectionsFullyLoaded = true
        }
      }))
      
      // Update writeable collections after collections change
      await get().updateWriteableCollections()
    }
    finally { set({processing:false}) }
  },


  addOrUpdateCollection : async (collection: CollectionInterface, newImage) => {
    if (get().profile?.uid == null) throw new Error("User not logged in")
    const newCollection:CollectionInterface = await updateOrSaveCollection(collection,newImage)
    set(produce( (state:any) => {
      const index = state.collections.findIndex((c:CollectionInterface) => c.id == newCollection.id) 
      if (index < 0) state.collections.unshift(newCollection)
      else  state.collections[index] = newCollection
    }))
    
    // Update the detailed collection store used by CollectionTab
    const collectionDetailedStore = useCollectionDetailed.getState();
    if (collectionDetailedStore.currentUserId) {
      const existingCollection = collectionDetailedStore.allCollections.find(c => c.id === newCollection.id);
      if (!existingCollection) {
        // This is a new collection - refresh the collections to include it
        collectionDetailedStore.refreshCollections();
      } else {
        // This is an update - update the existing collection
        collectionDetailedStore.updateCollection(newCollection);
      }
    }
    
    // Update writeable collections after adding/updating
    await get().updateWriteableCollections()
  },
  deleteCollection : async (collection: CollectionInterface, currentUserUid: string) => {
    if (!currentUserUid) throw new Error("User not logged in")
    
    // Check if user has permission to delete this collection
    const canDelete = await checkCollectionWritePermission(currentUserUid, collection)
    if (!canDelete) {
      throw new Error("You don't have permission to delete this collection")
    }
    
    try {
      await deleteCollection(collection)
      set(produce(  (state:any) => {
        state.collections = state.collections.filter((c:CollectionInterface) => c.id != collection.id)      
      }))
      
      // Update the detailed collection store used by CollectionTab
      const collectionDetailedStore = useCollectionDetailed.getState();
      if (collectionDetailedStore.currentUserId) {
        collectionDetailedStore.removeCollection(collection.id);
      }
      
      // Update writeable collections after deletion
      await get().updateWriteableCollections()
    } catch (error) {
      console.error('Error deleting collection:', error)
      throw new Error(`Failed to delete collection: ${error.message || 'Unknown error'}`)
    }
  },
  toggleProductInCollection : async (collectionID: string, productId: string, productThumbnail:string,userImage:CollectionUserImages, userMaterial:CollectionUserMaterials) => {
    const collection = get().collections.filter((c:CollectionInterface) => c.id == collectionID)?.[0]
    if (!collection) throw new Error("Collection not found")
    
    const currentUserUid = get().profile?.uid
    if (!currentUserUid) throw new Error("User not logged in")
    
    // Check if user has permission to modify this collection
    const canModify = await checkCollectionWritePermission(currentUserUid, collection)
    if (!canModify) throw new Error("User does not have permission to modify this collection") 

    const newCollection:CollectionInterface|any = produce(collection, (draft:CollectionInterface) => {
      if (!draft.products) draft.products = []

      if (productId) {
        if (draft.products.includes(productId)) {         
          draft.products = draft.products.filter((p:string) => p != productId) 
          const t =  draft.thumbnails?.filter((t:CollectionThumbnail) => t.id != productId)
          draft.thumbnails = t==null ? [] : t
        }
        else { 
          draft.products.push(productId) 
          if (!draft.thumbnails) draft.thumbnails = []
          draft.thumbnails?.push({id:productId, src:productThumbnail})
        }
      }
      else if (userImage) {
        if (!draft.userImages) draft.userImages = []
        if (draft.userImages.find(u => userImage.id == u.id)) {              
          draft.userImages = draft.userImages.filter((u:CollectionUserImages) => u.id != userImage.id)
          const t =  draft.thumbnails?.filter((t:CollectionThumbnail) => t.id != userImage.id)
          draft.thumbnails = t==null ? [] : t
        }
        else  {          
          draft.userImages.push(userImage)
          if (!draft.thumbnails) draft.thumbnails = []
          draft.thumbnails?.push({id:userImage.id, src:userImage.src})
        }
      }
      else if (userMaterial) {
        if (!draft.userMaterials) draft.userMaterials = []
        if (draft.userMaterials.find(u => userMaterial.id == u.id)) {              
          draft.userMaterials = draft.userMaterials.filter((u:CollectionUserMaterials) => u.id != userMaterial.id)
          const t =  draft.thumbnails?.filter((t:CollectionThumbnail) => t.id != userMaterial.id)
          draft.thumbnails = t==null ? [] : t
        }
        else  {          
          draft.userMaterials.push(userMaterial)
          if (!draft.thumbnails) draft.thumbnails = []
          draft.thumbnails?.push({id:userMaterial.id, src:userMaterial.src})
        }
      }
    })
    await updateOrSaveCollection(newCollection)
    set(produce( (state:any) => {
      const index = state.collections.findIndex((c:CollectionInterface) => c.id == collectionID) 
      state.collections[index] = newCollection
    }))
    
    // Update the detailed collection store used by CollectionTab
    const collectionDetailedStore = useCollectionDetailed.getState();
    if (collectionDetailedStore.currentUserId) {
      collectionDetailedStore.updateCollection(newCollection);
    }
    
    // Update writeable collections after toggling product
    await get().updateWriteableCollections()
  }
}))

// Helper function to check if user has write permission to a collection
async function checkCollectionWritePermission(userID: string, collection: CollectionInterface): Promise<boolean> {
  if (!userID || !collection) {
    return false;
  }

  // User owns the collection
  if (collection.uid === userID) {
    return true;
  }
  
  // Check if user has write access via the writeAccess array on the collection
  if (collection.writeAccess && Array.isArray(collection.writeAccess)) {
    return collection.writeAccess.includes(userID);
  }
  
  return false;
}

export async function getCollections (userID, collections:any, limitRecords: number) {
  const a:any = [userCollectionsDB, where("uid", "==", userID), orderBy("updatedAt","desc"), limit(limitRecords)]
  if (collections.length > 0) {
      a.splice(a.length-1, 0, startAfter(collections[collections.length-1].updatedAt))
  }
  const q = query.apply(this, a)
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => doc.data())
}

export async function getSharedCollections(userID: string, currentSharedCollections: CollectionInterface[], limitRecords: number): Promise<CollectionInterface[]> {
  try {
    // First, get the user's permissions to find which collections they can read
    const permissionsRef = doc(firebaseHelper.db, 'permissions', userID);
    const permissionsSnap = await getDoc(permissionsRef);
    
    if (!permissionsSnap.exists()) {
      return []; // No permissions found, return empty array
    }
    
    const permissions = permissionsSnap.data();
    const collectionRead = permissions?.collectionRead || [];
    const collectionWrite = permissions?.collectionWrite || [];
    
    // Combine read and write permissions, remove duplicates
    const allSharedCollectionIds = [...new Set([...collectionRead, ...collectionWrite])];
    
    if (allSharedCollectionIds.length === 0) {
      return []; // No shared collections
    }
    
    // For pagination, we need to handle this differently since we're querying by IDs
    // We'll fetch all shared collections and then handle pagination in memory
    let collectionIdsToFetch = allSharedCollectionIds;
    
    // If we have existing shared collections, we need to exclude them for pagination
    if (currentSharedCollections.length > 0) {
      const existingIds = currentSharedCollections.map(c => c.id);
      collectionIdsToFetch = allSharedCollectionIds.filter(id => !existingIds.includes(id));
    }
    
    if (collectionIdsToFetch.length === 0) {
      return []; // No new collections to fetch
    }
    
    // Firestore 'in' query has a limit of 10 items, so we need to batch
    const batchSize = 10;
    const allSharedCollections: CollectionInterface[] = [];
    
    for (let i = 0; i < collectionIdsToFetch.length; i += batchSize) {
      const batch = collectionIdsToFetch.slice(i, i + batchSize);
      
      if (!userCollectionsDB) {
        console.error('userCollectionsDB is not defined');
        continue;
      }
      
      const q = query(
        userCollectionsDB,
        where(documentId(), "in", batch),
        orderBy("updatedAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      const batchCollections = snapshot.docs.map(doc => doc.data() as CollectionInterface);
      allSharedCollections.push(...batchCollections);
      
      // Respect the limit
      if (allSharedCollections.length >= limitRecords) {
        break;
      }
    }
    
    // Sort by updatedAt and return only the requested limit
    return allSharedCollections
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, limitRecords);
      
  } catch (error) {
    console.error('Error fetching shared collections:', error);
    return [];
  }
}

export async function getOwnedProjectCollections(userID: string, currentOwnedProjectCollections: CollectionInterface[], limitRecords: number): Promise<CollectionInterface[]> {
  try {
    // Get projects owned by the user
    const projectsCollection = collection(firebaseHelper.db, 'projectsScheme');
    const ownedProjectsQuery = query(projectsCollection, where("owner", "==", userID));
    const ownedProjectsSnapshot = await getDocs(ownedProjectsQuery);
    
    // Filter out archived projects (where archive field is true)
    const ownedProjectIds = ownedProjectsSnapshot.docs
      .filter(doc => {
        const projectData = doc.data();
        return !projectData.archive; // Include only if archive is false or not present
      })
      .map(doc => doc.id);
    
    if (ownedProjectIds.length === 0) {
      return []; // No non-archived owned projects
    }

    if (!userCollectionsDB) {
      console.error('userCollectionsDB is not defined');
      return [];
    }
    
    // Query collections where associatedProjectId matches owned project IDs
    const allOwnedProjectCollections: CollectionInterface[] = [];
    
    // Firestore 'in' query has a limit of 10 items, so we need to batch
    const batchSize = 10;
    
    for (let i = 0; i < ownedProjectIds.length; i += batchSize) {
      const batch = ownedProjectIds.slice(i, i + batchSize);
      
      const q = query(
        userCollectionsDB,
        where("associatedProjectId", "in", batch),
        orderBy("updatedAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      const batchCollections = snapshot.docs.map(doc => doc.data() as CollectionInterface);
      allOwnedProjectCollections.push(...batchCollections);
      
      // Respect the limit early if we have enough
      if (allOwnedProjectCollections.length >= limitRecords * 2) { // Fetch more than needed for proper pagination
        break;
      }
    }
    
    // Remove duplicates and collections we already have
    const existingIds = currentOwnedProjectCollections.map(c => c.id);
    const newOwnedProjectCollections = allOwnedProjectCollections.filter(c => !existingIds.includes(c.id));
    
    // Sort by updatedAt and return only the requested limit
    return newOwnedProjectCollections
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, limitRecords);
      
  } catch (error) {
    console.error('Error fetching owned project collections:', error);
    return [];
  }
}

export async function getSharedProjectCollections(userID: string, currentSharedProjectCollections: CollectionInterface[], limitRecords: number): Promise<CollectionInterface[]> {
  try {
    // Get user's permissions to find which projects they can read
    const permissionsRef = doc(firebaseHelper.db, 'permissions', userID);
    const permissionsSnap = await getDoc(permissionsRef);
    
    if (!permissionsSnap.exists()) {
      return []; // No permissions found
    }
    
    const permissions = permissionsSnap.data();
    const projectRead = permissions?.projectRead || [];
    const projectWrite = permissions?.projectWrite || [];
    
    // Combine read and write permissions, remove duplicates
    const allSharedProjectIds = [...new Set([...projectRead, ...projectWrite])];
    
    if (allSharedProjectIds.length === 0) {
      return []; // No shared project access
    }

    // Filter out archived projects by checking the actual project documents
    const projectsCollection = collection(firebaseHelper.db, 'projectsScheme');
    const nonArchivedProjectIds: string[] = [];
    
    // Batch the project ID lookups (Firestore 'in' query limit is 10)
    const projectBatchSize = 10;
    for (let i = 0; i < allSharedProjectIds.length; i += projectBatchSize) {
      const batch = allSharedProjectIds.slice(i, i + projectBatchSize);
      const projectQuery = query(projectsCollection, where(documentId(), "in", batch));
      const projectSnapshot = await getDocs(projectQuery);
      
      projectSnapshot.docs.forEach(doc => {
        const projectData = doc.data();
        if (!projectData.archive) { // Include only if archive is false or not present
          nonArchivedProjectIds.push(doc.id);
        }
      });
    }
    
    if (nonArchivedProjectIds.length === 0) {
      return []; // No non-archived shared project access
    }

    if (!userCollectionsDB) {
      console.error('userCollectionsDB is not defined');
      return [];
    }
    
    // Query collections where associatedProjectId matches non-archived shared project IDs
    const allSharedProjectCollections: CollectionInterface[] = [];
    
    // Firestore 'in' query has a limit of 10 items, so we need to batch
    const batchSize = 10;
    
    for (let i = 0; i < nonArchivedProjectIds.length; i += batchSize) {
      const batch = nonArchivedProjectIds.slice(i, i + batchSize);
      
      const q = query(
        userCollectionsDB,
        where("associatedProjectId", "in", batch),
        orderBy("updatedAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      const batchCollections = snapshot.docs.map(doc => doc.data() as CollectionInterface);
      allSharedProjectCollections.push(...batchCollections);
      
      // Respect the limit early if we have enough
      if (allSharedProjectCollections.length >= limitRecords * 2) { // Fetch more than needed for proper pagination
        break;
      }
    }
    
    // Remove duplicates and collections we already have
    const existingIds = currentSharedProjectCollections.map(c => c.id);
    const newSharedProjectCollections = allSharedProjectCollections.filter(c => !existingIds.includes(c.id));
    
    // Sort by updatedAt and return only the requested limit
    return newSharedProjectCollections
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, limitRecords);
      
  } catch (error) {
    console.error('Error fetching shared project collections:', error);
    return [];
  }
}

export async function getProjectBasedCollections(userID: string, currentProjectCollections: CollectionInterface[], limitRecords: number): Promise<CollectionInterface[]> {
  try {
    // Fetch both owned and shared project collections
    const ownedProjectCollections = await getOwnedProjectCollections(userID, currentProjectCollections.filter(c => c.associatedProjectId), Math.ceil(limitRecords/2));
    const sharedProjectCollections = await getSharedProjectCollections(userID, currentProjectCollections.filter(c => c.associatedProjectId), Math.ceil(limitRecords/2));
    
    // Combine all project collections
    const allProjectCollections = [...ownedProjectCollections, ...sharedProjectCollections];
    
    // Remove duplicates based on ID
    const uniqueProjectCollections = allProjectCollections.filter((collection, index, self) => 
      index === self.findIndex(c => c.id === collection.id)
    );
    
    // Sort by updatedAt and return only the requested limit
    return uniqueProjectCollections
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, limitRecords);
      
  } catch (error) {
    console.error('Error fetching project-based collections:', error);
    return [];
  }
}


async function updateOrSaveCollection(collection:CollectionInterface, newFileBlob=null): Promise<CollectionInterface>  {
  return new Promise(async (resolve, reject) => {
      validateCollection(collection)
      
      let url:any = null;
      if (newFileBlob) { url = await uploadImage(collection, newFileBlob) }
      const newCollection = produce(collection, (draft:CollectionInterface) => {
        // if (newFileBlob)  //commented it to delete the old image
          if(newFileBlob) draft.coverImage = url
      })
      updateRecord(collection.id, "usersMyCollection", newCollection).then(result=>{
          // console.log("Collection updated!", collection);
          resolve(newCollection as CollectionInterface);
      }).catch(error=> { console.log("error ", error); reject(error) })
    })
}

//a wrapper for async stuff
async function uploadImage(collection:CollectionInterface,blob) {
  return new Promise((resolve, reject) => {
    uploadFileToGCloud(`userImages/${collection.uid}`, `collection_cover_${generateID()}`, blob).then(url => resolve(url)) 
    .catch(error => reject(error));
  })
}

async function deleteCollection (collection: CollectionInterface) {
  try {
    if (!collection?.id) {
      throw new Error("Collection ID is required for deletion")
    }
    
    await deleteRecordAndFiles(collection.id, "usersMyCollection")
    return collection
  } catch (error) {
    console.error("Error deleting collection:", error)
    throw new Error(error.message || "Failed to delete collection from database")
  }
}

let globalProfileIsProcessing=false; 
const  createProfileIfNotExists = async (firebaseData,mailingList)=>   {
  try {
    console.log("Visiting craeteProfile if not exists");
    if(globalProfileIsProcessing==true) { return;  }
    globalProfileIsProcessing = true;
    if ( (!firebaseData?.user?.uid) || (!firebaseData?.user.email)) return null;   
    const uid = firebaseData.user.uid;
    const email = firebaseData.user.email;
    if (profilesDB==null) throw new Error("profilesDB is not defined")
    const q = query(profilesDB, where("uid", "==", uid));
    const querySnapshot = await getDocs(q);    
    if(querySnapshot.metadata.fromCache) {
      globalProfileIsProcessing = false;
      return;
    }
    if (querySnapshot.docs.length > 0) { 
      const profile = querySnapshot.docs[0].data() as ProfileInterface
      if(!profile?.country) {
        const newProfile: ProfileInterface = await getUserCountry(querySnapshot);        
        return {ref:querySnapshot.docs[0].ref, updated:false, profile:newProfile} 
      }
      return {ref:querySnapshot.docs[0].ref, updated:false, profile:profile} 
    }
    else {       
      console.log("Now I am creating a new profile record");
      let country:any = 'United States'
      try {       
        const resp:any= await getUserLocationFromIPdata(2000);  
        country = resp?.country_name || 'United States'
      }
      catch (err) { console.log('Could not get user location'); }      
      const isGoogle = firebaseData.providerId=='google.com'
      const first_name = isGoogle ? firebaseData?._tokenResponse?.firstName : ''; 
      const last_name = isGoogle ? firebaseData?._tokenResponse?.lastName : '';  
      if (!uid || !email ) { throw new Error("uid and email are required"); }
      if (uid?.length < 5 || email?.length < 3) { throw new Error("uid and email are required"); }  
  
      const measuringCategoryValue = country == 'United States'?  MeasuringCategory.IMPERIAL: MeasuringCategory.METRIC
      const profile:ProfileInterface = {id:generateID(), uid:uid, email:email,country:country,
        measuring_category:measuringCategoryValue,
        measuringCategory:measuringCategoryValue,
        renders:0,
        mailingList:mailingList,createdAt:new Date(), created_at:new Date()}
      if(first_name?.length > 1) profile['first_name'] = first_name;
      if(last_name?.length > 1) profile['last_name'] = last_name;
        //create ref here. 
      const firebaseRef = doc(firebaseDB as any, 'profiles', profile.id as string)
      const result = await updateProfile(profile,{},firebaseRef)
      return {ref:firebaseRef,  updated:true, profile:result as ProfileInterface}
      }
    }
    catch (e) { console.log(e) } 
    finally { 
      globalProfileIsProcessing = false;
    }
}

const getProfile = async (firebaseData) => {
  try {
    if (globalProfileIsProcessing==true) { return;  }
    globalProfileIsProcessing = true;
    if ( (!firebaseData?.user?.uid) || (!firebaseData?.user.email)) return null;   
    const uid = firebaseData.user.uid;
    if (profilesDB==null) throw new Error("profilesDB is not defined")
    const q = query(profilesDB, where("uid", "==", uid));
    const querySnapshot = await getDocs(q);    
    // console.log('how many times here?');
    if (querySnapshot.docs.length > 0) { 
      const profile = querySnapshot.docs[0].data() as ProfileInterface
      if(!profile?.country) {
        const newProfile: ProfileInterface = await getUserCountry(querySnapshot);        
        return {ref:querySnapshot.docs[0].ref, updated:false, profile:newProfile} 
      }
      return {ref:querySnapshot.docs[0].ref, updated:false, profile:querySnapshot.docs[0].data() as ProfileInterface} 
    }
    else return null;
  }
  finally {
    globalProfileIsProcessing = false;
  }
}

const getUserCountry = async (querySnapshot: any) => {
  const profile = querySnapshot.docs[0].data() as ProfileInterface;
  let country:any = 'United States'
  try {       
    const resp:any= await getUserLocationFromIPdata(2000);  
    country = resp?.country_name || 'United States'
    const newProfile:ProfileInterface = produce(profile, (draft:ProfileInterface) => {
      draft.country = country
    })
    await updateProfile(newProfile,{},querySnapshot.docs[0].ref)
    return newProfile;
  } catch (err) { console.log('Could not get user location'); }
  return profile;
}